import React, { useMemo } from "react";
import { Stack, Text, Table } from "@mantine/core";
import _ from "lodash";
import { getMappableFieldByPath } from "../../data/orderServiceMappableFields";
import { formatOrderStatusLabel } from "../../data/options";

const formatStatus = (value) => formatOrderStatusLabel(value);

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (field?.fieldType === "SELECT_ORDER_STATUS" || field?.orderFieldPath === "status") {
    return formatStatus(value);
  }
  if (field?.fieldType === "SELECT") {
    const option = (field.selectOptions || []).find(
      (o) => (o.value ?? o.label) === value
    );
    if (option) return option.label;
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object") {
    if (value.line1 || value.city) {
      return [value.line1, value.line2, value.city, value.state, value.zip, value.country]
        .filter(Boolean)
        .join(", ");
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const formatPreviewValue = (value, orderFieldPath) => {
  if (value === null || value === undefined || value === "") return "-";
  if (orderFieldPath === "status") return formatStatus(value);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object") {
    if (value.line1 || value.city) {
      return [value.line1, value.line2, value.city, value.state, value.zip, value.country]
        .filter(Boolean)
        .join(", ");
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const ServiceTypeCompleteSummary = ({ form }) => {
  const rows = useMemo(() => {
    const snapshot = form?.values?.serviceTypeSnapShot || {};
    const fields = snapshot.fields || [];
    const completeOrderUpdates = snapshot.completeOrderUpdates || [];
    const fieldValues = form?.values?.fieldValues || {};
    const order = form?.values?.orderId || {};
    const fieldByKey = Object.fromEntries(fields.map((f) => [f.key, f]));

    const fromLegacyFields = fields
      .filter((field) => field?.orderFieldPath)
      .map((field) => {
        const mapped = getMappableFieldByPath(field.orderFieldPath);
        return {
          key: `legacy-field-${field.key}`,
          label: mapped?.label || field.orderFieldPath,
          source: field.label,
          current: formatPreviewValue(
            _.get(order, field.orderFieldPath),
            field.orderFieldPath
          ),
          next: formatFieldValue(field, fieldValues[field.key]),
        };
      });

    const fromComplete = completeOrderUpdates
      .filter((row) => row?.orderFieldPath)
      .map((row, index) => {
        const mapped = getMappableFieldByPath(row.orderFieldPath);
        const valueSource =
          row.valueSource === "serviceField" ? "serviceField" : "fixed";
        const sourceField =
          valueSource === "serviceField"
            ? fieldByKey[row.serviceFieldKey]
            : null;
        const nextValue =
          valueSource === "serviceField"
            ? fieldValues[row.serviceFieldKey]
            : row.value;

        return {
          key: `complete-${row.orderFieldPath}-${index}`,
          label: mapped?.label || row.orderFieldPath,
          source:
            valueSource === "serviceField"
              ? sourceField?.label || row.serviceFieldKey
              : "Fixed value",
          current: formatPreviewValue(
            _.get(order, row.orderFieldPath),
            row.orderFieldPath
          ),
          next: sourceField
            ? formatFieldValue(sourceField, nextValue)
            : formatPreviewValue(nextValue, row.orderFieldPath),
        };
      });

    return [...fromLegacyFields, ...fromComplete];
  }, [form?.values]);

  if (!rows.length) {
    return (
      <Text size="sm" color="dimmed">
        Completing this service will not update any order fields.
      </Text>
    );
  }

  return (
    <Stack spacing="sm">
      <Text size="sm">
        The following order fields will be updated when you complete this service:
      </Text>
      <Table fontSize="sm">
        <thead>
          <tr>
            <th>Order Field</th>
            <th>Source</th>
            <th>Current</th>
            <th>New Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td>{row.source}</td>
              <td>{row.current}</td>
              <td>{row.next}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Stack>
  );
};

export default ServiceTypeCompleteSummary;
